import { useState } from 'react'
import { Box, Chip, CircularProgress, Collapse, IconButton, Tooltip, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import SubjectIcon from '@mui/icons-material/Subject'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import FancyProgressArc from './FancyProgressArc'
import { SourceDoc } from '../types'
import { FS_MICRO, FS_SM, FS_XL, ICON_XS } from '../common/theme'

interface TaFoundDocProps {
  source: SourceDoc
  canDelete?: boolean
  onDelete?: () => void
  index?: number
}

export default function TaFoundDoc({ source, canDelete, onDelete, index }: TaFoundDocProps) {
  const [showDrop, setShowDrop] = useState((source as any).showdrop ?? false)
  const linkId = `founddoc_${index}`
  const wordCount = (source as any).totalwords !== undefined
    ? Number((source as any).totalwords).toLocaleString()
    : ''

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.75,
          opacity: (source as any).isdeleted ? 0.45 : 1,
          bgcolor: (source as any).isselected ? 'action.selected' : undefined,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <FancyProgressArc value={(source as any).simint ?? 0} zerona scale={0.65} tooltip="Source Suitability" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontWeight: 500, fontSize: '0.74rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <span dangerouslySetInnerHTML={{ __html: source.filename as string }} />
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
            {!(source as any).projectfile && (source as any).path && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <FolderOutlinedIcon sx={{ fontSize: 11, opacity: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6 }}>{(source as any).path}</Typography>
              </Box>
            )}
            {wordCount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <SubjectIcon sx={{ fontSize: 11, opacity: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6 }}>
                  {(source as any).isdeleted ? '?' : wordCount}
                </Typography>
              </Box>
            )}
            {(source as any).uploaded && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <CloudUploadOutlinedIcon sx={{ fontSize: 11, opacity: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6 }}>
                  {(source as any).isdeleted ? '?' : (source as any).uploaded}
                </Typography>
              </Box>
            )}
            {(source as any).partialexplain && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <MenuBookOutlinedIcon sx={{ fontSize: 11, opacity: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6 }}>
                  {(source as any).isdeleted ? '?' : (source as any).partialexplain}
                </Typography>
              </Box>
            )}
            {(source as any).usefulcontent && (
              <Chip size="small" label="RELEVANT" color="success" sx={{ height: 14, fontSize: '0.58rem' }} />
            )}
            {(source as any).used && (
              <Chip size="small" label="USED" sx={{ height: 14, fontSize: '0.58rem' }} />
            )}
          </Box>
        </Box>

        {!(source as any).isdeleted && (
          <Tooltip title={showDrop ? 'Hide Segments' : 'Show Segments'}>
            <IconButton size="small" onClick={() => setShowDrop((v: boolean) => !v)} sx={{ p: 0.25, flexShrink: 0 }}>
              {showDrop ? <ExpandLessIcon sx={{ fontSize: FS_XL }} /> : <ExpandMoreIcon sx={{ fontSize: FS_XL }} />}
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Remove">
            <IconButton size="small" onClick={onDelete} sx={{ p: 0.25, flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: ICON_XS }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Collapse in={showDrop}>
        <Box
          id={`${linkId}_${(source as any).sequencesha}_drop`}
          sx={{ borderTop: 1, borderColor: 'divider', px: 1.5, py: 0.75, bgcolor: 'action.hover' }}
        >
          {(source as any).isprocessing && <CircularProgress size={14} sx={{ mb: 0.5 }} />}
          {(source as any).segments?.map((seg: any, si: number) =>
            seg.psstructure?.paragraphs?.map((para: any, pi: number) => (
              <Box key={`${si}_${pi}`} sx={{ mb: 0.5 }}>
                {para.lines?.map((line: any, li: number) => (
                  <Box key={li}>
                    {line.sentences?.map((sent: any, senti: number) => (
                      <Box key={senti} component="span" sx={{ fontSize: FS_SM, lineHeight: 1.6 }}>
                        <span dangerouslySetInnerHTML={{ __html: sent.sentence }} />
                        {sent.citations > 0 && (
                          <Typography component="span" sx={{ fontSize: FS_MICRO, opacity: 0.5, ml: 0.25 }}>
                            x{sent.citations}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
